import { Button, Col, Row } from 'react-bootstrap';
import { organizationDetailsSignal } from '../OrganizationsList.signal';
import { addHttpsToUrl, enumCaseToTabCase } from 'libs/string';
import { ASSISTANCE_USES } from '../../Constant/constants';
import { createGoogleMapsLink } from 'utils/queryAddresses';
import currencyOptions from '../../Constant/currencyOptions';
import { camelCaseToTitleText, formatMinorityStatusValue } from '../../../../utils/formatTxt';
import {
  getDemographicsLabel,
  GENDER_LABELS,
  SEXUAL_ORIENTATION_LABELS,
  VETERAN_STATUS_LABELS,
  ETHNICITY_LABELS,
  MIDDLE_EASTERN_OR_NORTH_AFRICAN_LABELS,
} from '../../../../constants/demographicsLabels';
import { Business } from '../../../../types';
import DownloadableDocumentsRow from '../../DownloadableDocumentsRow';

const ASSISTANCE_USE_LABELS = Object.values(ASSISTANCE_USES).reduce<Record<string, string>>(
  (acc, option) => {
    acc[option.value] = option.label;
    return acc;
  },
  {}
);

/** Build display address from formattedAddress (v2 API or address object) or from address line + city/state/zip. */
function getBusinessDisplayAddress(business: Business): string {
  const formatted = business.formattedAddress ?? business.address?.formattedAddress;
  if (formatted?.trim()) return formatted;
  const parts = [
    business.addressLine1,
    business.addressLine2,
    [business.city, business.state, business.zipCode].filter(Boolean).join(', '),
  ].filter(Boolean);
  return parts.join(', ').trim() || '';
}

/** Prefer Business.ein; if missing and this is a CPCFA import row, show misc.einFromImport (FRED tax id). */
function getBusinessDisplayEin(business: Business): string {
  const direct = typeof business.ein === 'string' ? business.ein.trim() : '';
  if (direct) return direct;
  const fromImport = business.misc?.einFromImport;
  if (fromImport == null || fromImport === '') return '';
  return String(fromImport).trim();
}

type Props = {
  hideOrganizationName?: boolean;
  headerButton?: {
    label: string;
    onClick: () => void;
    disabled: boolean;
  } | null;
  secondaryHeaderButton?: {
    label: string;
    onClick: () => void;
    disabled: boolean;
  } | null;
};

const BusinessDetails: React.FC<Props> = ({
  hideOrganizationName,
  headerButton = null,
  secondaryHeaderButton = null,
}) => {
  const business = organizationDetailsSignal.value
    .selectedOrganization as Business;
  const {
    assistance,
    businessDocuments,
    demographics,
    misc,
    sediCertification,
    employment,
    minorityStatus,
  } = business;
  const displayEin = getBusinessDisplayEin(business);

  return (
    <div>
      <div className="d-flex flex-row justify-content-between align-items-center">
        <div>
          <h3 hidden={hideOrganizationName}>
            {business.name ||
              (displayEin
                ? `Business with EIN: ${displayEin}`
                : `Business with ID: ${business.id}`)}
          </h3>
          <p className="text-muted mb-0 mt-1">This is the latest details for this business.</p>
        </div>
        {(!!headerButton || !!secondaryHeaderButton) && (
          <div className="d-flex">
            {!!secondaryHeaderButton && (
              <Button
                variant="outline-primary"
                disabled={secondaryHeaderButton.disabled}
                className="rounded mr-2"
                onClick={secondaryHeaderButton.onClick}
              >
                {secondaryHeaderButton.label}
              </Button>
            )}
            {!!headerButton && (
              <Button
                disabled={headerButton.disabled}
                className="rounded"
                onClick={headerButton.onClick}
              >
                {headerButton.label}
              </Button>
            )}
          </div>
        )}
      </div>
      <hr />
      <Row>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Contact Name</p>
          {business.contactName || 'Unknown'}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Email</p>
          {business.contactEmail ? (
            <a href={`mailto:${business.contactEmail}`}>
              {business.contactEmail}
            </a>
          ) : (
            'Unknown'
          )}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Phone Number</p>
          {business.phoneNumber ? (
            <a href={`tel:${business.phoneNumber}`}>{business.phoneNumber}</a>
          ) : (
            'Unknown'
          )}
        </Col>
        <Col  xs={6} lg className="text-overflow-ellipsis mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Website</p>
          {business.website ? (
            <a
              href={addHttpsToUrl(business.website)}
              target="_blank"
              rel="noopener noreferrer"
            >
              {business.website}
            </a>
          ) : (
            'Unknown'
          )}
        </Col>
      </Row>
      <Row className="mt-32">
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Address</p>
          <p className="mb-0">
            {getBusinessDisplayAddress(business) ? (
              <a
                href={createGoogleMapsLink(getBusinessDisplayAddress(business))}
                target="_blank"
                rel="noopener noreferrer"
              >
                {getBusinessDisplayAddress(business)}
              </a>
            ) : (
              'Unknown'
            )}
          </p>
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">EIN</p>
          {displayEin || 'Unknown'}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">NAICS Code</p>
          {business.naicsCode || 'Unknown'}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Census Tract</p>
          {business.censusTract || 'Unknown'}
        </Col>
      </Row>
      <Row className="mt-32">
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Description</p>
          {(business.description?.trim() || business.naicsDescription) ?? 'Unknown'}
        </Col>
        <Col xs={9}  className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Industry</p>
          {(business.businessTypeDesc?.trim() ||
            (assistance?.industrySelection?.length
              ? assistance.industrySelection.join(', ')
              : null) ||
            business.naicsSector) ?? 'Unknown'}
        </Col>
      </Row>
      <Row className="mt-32">
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Start Up</p>
          {camelCaseToTitleText(misc.isStartUp || 'Unknown')}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Climate Aligned Investment</p>
          {camelCaseToTitleText(misc.climateAlignedInvestment || 'Unknown')}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">
            Energy or Climate-Impacted Communities
          </p>
          {camelCaseToTitleText(
            misc.energyOrClimateImpactedCommunities || 'Unknown'
          )}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Business plan</p>
          {camelCaseToTitleText(misc.hasBusinessPlan || 'Unknown')}
        </Col>
      </Row>
      <h4 className="mt-48">Assistance</h4>
      <hr />
      <Row>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Funding Range</p>
          {assistance.seekingMin != null && assistance.seekingMax != null
            ? `${Number(assistance.seekingMin).toLocaleString(
                'en-US',
                currencyOptions.currencyUSNoDecimal as Intl.NumberFormatOptions
              )} - ${Number(assistance.seekingMax).toLocaleString(
                'en-US',
                currencyOptions.currencyUSNoDecimal as Intl.NumberFormatOptions
              )}`
            : assistance.seekingMin != null
              ? Number(assistance.seekingMin).toLocaleString(
                  'en-US',
                  currencyOptions.currencyUSNoDecimal as Intl.NumberFormatOptions
                )
              : assistance.seekingMax != null
                ? Number(assistance.seekingMax).toLocaleString(
                    'en-US',
                    currencyOptions.currencyUSNoDecimal as Intl.NumberFormatOptions
                  )
                : 'Unknown'}
        </Col>

        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Assistance Needed</p>
          {assistance.desiredAssistanceTypes &&
          assistance.desiredAssistanceTypes.length > 0
            ? assistance.desiredAssistanceTypes
                .map((s) => camelCaseToTitleText(s))
                .join(', ')
            : 'Unknown'}
        </Col>

        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Assistance Use</p>
          {assistance.assistanceUses && assistance.assistanceUses.length > 0
            ? assistance.assistanceUses
                .map((s) => ASSISTANCE_USE_LABELS[s] || enumCaseToTabCase(s))
                .join(', ')
            : 'Unknown'}
        </Col>
      </Row>
      <h4 className="mt-48">SEDI Certification</h4>
      <hr />
      <Row>
        <Col>
          <p className="font-weight-bold mb-0">Self Certified</p>
          {camelCaseToTitleText(sediCertification.isSediOwned || 'Unknown')}
        </Col>
      </Row>
      <Row className="mt-32">
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">
            SEDI Status by Business Address in CDFI Investment Area
          </p>
          {camelCaseToTitleText(
            sediCertification.sediStatusByBusinessAddressInCDFIInvestmentArea ||
              'Unknown'
          )}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">
            Self-Certified SEDI Owned and Controlled in CDFI Investment Area
          </p>
          {sediCertification.selfCertifiedSEDIOwnedAndControlledInCDFIInvestmentArea ||
            'Unknown'}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">
            Self-certified SEDI Future Location in CDFI Investment Area
          </p>
          {sediCertification.selfCertifiedSEDIFutureLocationInCDFIInvestmentArea ||
            'Unknown'}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">
            Self-Certified SEDI Demographics-Related Business Status
          </p>
          {renderSediDemographicsStatusValue(
            sediCertification.selfCertifiedSEDIDemographicsRelatedBusinessStatus
          )}
        </Col>
      </Row>
      <h4 className="mt-48">Minority Status</h4>
      <hr />
      <Row>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Minority Owned</p>
          {minorityStatus.isMinorityOwnedLabel ??
            (formatMinorityStatusValue(minorityStatus.isMinorityOwned ?? '') || 'Unknown')}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Woman Owned</p>
          {minorityStatus.isWomanOwnedLabel ??
            (formatMinorityStatusValue(minorityStatus.isWomanOwned ?? '') || 'Unknown')}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Veteran Owned</p>
          {minorityStatus.isVeteranOwnedLabel ??
            (formatMinorityStatusValue(minorityStatus.isVeteranOwned ?? '') || 'Unknown')}
        </Col>
      </Row>
      <h4 className="mt-48">Employment</h4>
      <hr />
      <Row>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Number of Employees</p>
          {employment.numberOfEmployees || 'Unknown'}
        </Col>

        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Number of Full-time Employees</p>
          {employment.numberOfFullTimeEmployees || 'Unknown'}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Number of Part-time Employees</p>
          {employment.numberOfPartTimeEmployees || 'Unknown'}
        </Col>
      </Row>
      <Row className="mt-32">
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Expected Jobs Created</p>
          {employment.expectedJobsCreated || 'Unknown'}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Expected Jobs Retained</p>
          {employment.expectedJobsRetained || 'Unknown'}
        </Col>
      </Row>
      <h4 className="mt-48">Demographics</h4>
      <hr />
      <Row>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Ethnicity</p>
          {renderEthnicityValue(demographics)}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Race</p>
          {renderRaceValue(demographics.race)}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">
            Middle Eastern or North African Ancestry
          </p>
          {renderMiddleEasternOrNorthAfricanAncestryValue(demographics)}
        </Col>
      </Row>
      <Row className="mt-24">
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Gender</p>
          {renderGenderValue(demographics)}
        </Col>
        <Col xs={6} lg>
          <p className="font-weight-bold mb-0">Sexual Orientation</p>
          {renderSexualOrientationValue(demographics)}
        </Col>
        <Col xs={6} lg className="mt-24 mt-lg-0">
          <p className="font-weight-bold mb-0">Veteran Status</p>
          {renderVeteranStatusValue(demographics)}
        </Col>
      </Row>
      <h4 className="mt-48">Documents</h4>
      <hr />
      <DownloadableDocumentsRow
        overarchingDocumentType={'businessDocuments'}
        documents={businessDocuments.map((businessDocument) => ({
          filePath: businessDocument.filePath,
          fileType: businessDocument.fileType,
          header: businessDocument.documentType
            ? 'Suggested Document'
            : 'Additional Document',
          id: businessDocument.id,
          name: businessDocument.name,
        }))}
      />
    </div>
  );
};

function renderSexualOrientationValue(demographics: Business['demographics']) {
  if (!demographics.sexualOrientation) {
    return 'Unknown';
  }
  return (
    getDemographicsLabel(
      demographics.sexualOrientation,
      SEXUAL_ORIENTATION_LABELS,
      formatMinorityStatusValue
    ) || 'Unknown'
  );
}

function renderVeteranStatusValue(demographics: Business['demographics']) {
  if (!demographics.veteranStatus) {
    return 'Unknown';
  }
  return (
    getDemographicsLabel(
      demographics.veteranStatus,
      VETERAN_STATUS_LABELS,
      formatMinorityStatusValue
    ) || 'Unknown'
  );
}

function renderGenderValue(demographics: Business['demographics']) {
  if (!demographics.gender) {
    return 'Unknown';
  }
  return (
    getDemographicsLabel(
      demographics.gender,
      GENDER_LABELS,
      formatMinorityStatusValue
    ) || 'Unknown'
  );
}

function renderEthnicityValue(demographics: Business['demographics']) {
  if (!demographics.ethnicity?.hispanicOrLatino) {
    return 'Unknown';
  }
  const v = demographics.ethnicity.hispanicOrLatino;
  if (v === 'decline') return 'Prefer not to respond';
  if (v === 'no') return 'Not Hispanic or Latino';
  if (v === 'yes') return 'Hispanic or Latino';
  return (
    getDemographicsLabel(v, ETHNICITY_LABELS, formatMinorityStatusValue) ||
    'Unknown'
  );
}

function renderMiddleEasternOrNorthAfricanAncestryValue(
  demographics: Business['demographics']
) {
  const value =
    demographics.middleEasternOrNorthAfricanAncestry?.middleEasternOrNorthAfrican;
  if (!value) return 'Unknown';
  if (value === 'decline') return 'Prefer not to respond';
  return (
    getDemographicsLabel(
      value,
      MIDDLE_EASTERN_OR_NORTH_AFRICAN_LABELS,
      formatMinorityStatusValue
    ) || 'Unknown'
  );
}

function renderSediDemographicsStatusValue(
  value: Business['sediCertification']['selfCertifiedSEDIDemographicsRelatedBusinessStatus']
) {
  if (!value) return 'Unknown';
  if (Array.isArray(value)) {
    const entries = value.filter(Boolean);
    return entries.length ? entries.join(', ') : 'Unknown';
  }
  return value;
}

function renderRaceValue(race: Business['demographics']['race']) {
  if (!race) {
    return 'Unknown';
  }

  if (race.preferNotToRespond) {
    return 'Prefer not to respond';
  }

  const races = [];
  if (race.americanIndianOrAlaskaNative) {
    races.push('American Indian or Alaska Native');
  }

  if (race.asianIndian) {
    races.push('Asian Indian');
  }

  if (race.asianChinese) {
    races.push('Asian Chinese');
  }

  if (race.asianFilipino) {
    races.push('Asian Filipino');
  }

  if (race.asianJapanese) {
    races.push('Asian Japanese');
  }

  if (race.asianKorean) {
    races.push('Asian Korean');
  }

  if (race.asianVietnamese) {
    races.push('Asian Vietnamese');
  }

  if (race.asianOther) {
    races.push('Asian Other');
  }

  if (race.blackOrAfricanAmerican) {
    races.push('Black or African American');
  }

  if (race.guamanianOrChamorro) {
    races.push('Guamanian or Chamorro');
  }

  if (race.nativeHawaiian) {
    races.push('Native Hawaiian');
  }

  if (race.samoan) {
    races.push('Samoan');
  }

  if (race.pacificIslanderOther) {
    races.push('Pacific Islander Other');
  }

  if (race.white) {
    races.push('White');
  }

  const joined = races.join(', ');
  return joined || 'Unknown';
}

export default BusinessDetails;
